import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  updateGuaranteesUmvnApprovalStatus,
  updateGuaranteesUdbtfApprovalStatus,
  updateGuaranteesUoApprovalStatus,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesFetchStatus,
  GuaranteesUdbtfApprovalStatus,
  GuaranteesUmvnApprovalStatus,
  GuaranteesUoApprovalStatus,
  type TGuaranteesApplication,
} from '~/store/guarantees/types.ts';
import {
  selectGuaranteesUpdateUdbtfApprovalStatusStatus,
  selectGuaranteesUpdateUmvnApprovalStatusStatus,
  selectGuaranteesUpdateUoApprovalStatusStatus,
} from '~/store/guarantees/selectors.ts';
import type { AppDispatch } from '~/store/store.ts';
import type { TGuaranteesModalSuccessState } from '../../types.ts';
import {
  UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT,
  UDBTF_APPROVAL_LETTER_TEXT,
} from './constants.ts';
import type { TInternalApprovalType } from './types.ts';

type TUseInternalApprovalStatusUpdateParams = {
  application?: TGuaranteesApplication | null;
  approvalType: TInternalApprovalType;
  dispatch: AppDispatch;
  effectiveApplicationId: string;
  navigateToTasks: () => void;
  setModalAlert: (value: { text: string }) => void;
  setModalSuccess: (value: TGuaranteesModalSuccessState) => void;
  showGuaranteesRequestError: (error: unknown) => void;
};

const getApplicationVersion = (application?: TGuaranteesApplication | null): number | null =>
  typeof application?.version === 'number' && Number.isFinite(application.version)
    ? application.version
    : null;

export const useInternalApprovalStatusUpdate = ({
  application,
  approvalType,
  dispatch,
  effectiveApplicationId,
  navigateToTasks,
  setModalAlert,
  setModalSuccess,
  showGuaranteesRequestError,
}: TUseInternalApprovalStatusUpdateParams) => {
  const updateUdbtfApprovalStatusStatus = useSelector(
    selectGuaranteesUpdateUdbtfApprovalStatusStatus
  );
  const updateUoApprovalStatusStatus = useSelector(selectGuaranteesUpdateUoApprovalStatusStatus);
  const updateUmvnApprovalStatusStatus = useSelector(
    selectGuaranteesUpdateUmvnApprovalStatusStatus
  );
  const isApprovalStatusUpdating =
    (approvalType === 'udbtf' &&
      updateUdbtfApprovalStatusStatus === GuaranteesFetchStatus.Loading) ||
    (approvalType === 'uo' && updateUoApprovalStatusStatus === GuaranteesFetchStatus.Loading) ||
    (approvalType === 'umvn' &&
      updateUmvnApprovalStatusStatus === GuaranteesFetchStatus.Loading);

  const updateApprovalStatus = useCallback(
    async ({
      description,
      status,
      successText,
      successModalAction,
      versionOverride,
    }: {
      description?: string;
      status:
        | GuaranteesUdbtfApprovalStatus
        | GuaranteesUoApprovalStatus
        | GuaranteesUmvnApprovalStatus;
      successText: string;
      successModalAction?: () => void;
      versionOverride?: number;
    }) => {
      const version = versionOverride ?? getApplicationVersion(application);

      if (version === null) {
        setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT });

        return false;
      }

      try {
        if (approvalType === 'udbtf') {
          await dispatch(
            updateGuaranteesUdbtfApprovalStatus({
              applicationApprovalStatus: status as GuaranteesUdbtfApprovalStatus,
              applicationId: effectiveApplicationId,
              description,
              version,
            })
          ).unwrap();
        } else if (approvalType === 'uo') {
          await dispatch(
            updateGuaranteesUoApprovalStatus({
              applicationApprovalStatus: status as GuaranteesUoApprovalStatus,
              applicationId: effectiveApplicationId,
              description,
              version,
            })
          ).unwrap();
        } else if (approvalType === 'umvn') {
          await dispatch(
            updateGuaranteesUmvnApprovalStatus({
              applicationApprovalStatus: status as GuaranteesUmvnApprovalStatus,
              applicationId: effectiveApplicationId,
              description,
              version,
            })
          ).unwrap();
        } else {
          setModalSuccess({
            text: successText,
            buttonValue: UDBTF_APPROVAL_LETTER_TEXT.successCloseButton,
            onClick: successModalAction,
            onClose: successModalAction,
          });

          return true;
        }

        setModalSuccess({
          text: successText,
          buttonValue: UDBTF_APPROVAL_LETTER_TEXT.successCloseButton,
          onClick: successModalAction,
          onClose: successModalAction,
        });

        return true;
      } catch (error) {
        showGuaranteesRequestError(error);

        return false;
      }
    },
    [
      application,
      approvalType,
      dispatch,
      effectiveApplicationId,
      setModalAlert,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );

  const approveApplication = useCallback(
    (versionOverride?: number) => {
      return updateApprovalStatus({
        status:
          approvalType === 'uo'
            ? GuaranteesUoApprovalStatus.APPROVED
            : approvalType === 'umvn'
              ? GuaranteesUmvnApprovalStatus.APPROVED
              : GuaranteesUdbtfApprovalStatus.APPROVED,
        successText: UDBTF_APPROVAL_LETTER_TEXT.signingSuccessMessage,
        successModalAction: navigateToTasks,
        versionOverride,
      });
    },
    [approvalType, navigateToTasks, updateApprovalStatus]
  );

  const denyApplication = useCallback(
    (description: string) => {
      return updateApprovalStatus({
        description: description.trim(),
        status:
          approvalType === 'uo'
            ? GuaranteesUoApprovalStatus.DENIED
            : approvalType === 'umvn'
              ? GuaranteesUmvnApprovalStatus.DENIED
              : GuaranteesUdbtfApprovalStatus.DENIED,
        successText: UDBTF_APPROVAL_LETTER_TEXT.revokeSuccessMessage,
      });
    },
    [approvalType, updateApprovalStatus]
  );

  return {
    approveApplication,
    denyApplication,
    isUdbtfApprovalStatusUpdating: isApprovalStatusUpdating,
  };
};
